import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { GET_DASHBOARD_V2_PERFORMANCE } from 'actions/dashboardV2';
import Card from 'components/inputs/Card';
import Loader from 'components/general/Loader';
import { RANGE_LABEL_STYLE, getRangeLabel } from './dateRanges';

// Column -> date-range key, so the exact window used for each figure is visible.
const COLUMN_RANGE_KEYS = { Yesterday: 'yesterday', 'Last Week': 'last_week', 'Last 30 Days': '30d' };

const RangeCaption = ({ label }) => (
  <div style={{ ...RANGE_LABEL_STYLE, fontSize: 10, marginTop: 1 }}>{getRangeLabel(COLUMN_RANGE_KEYS[label])}</div>
);

RangeCaption.propTypes = { label: PropTypes.string };

const MetricCard = ({ title, yesterday, lastWeek, last30d, unit }) => (
  <Card className="h-100 w-100">
    <div style={{ padding: '8px 4px' }}>
      <h6 style={{ fontWeight: 600, marginBottom: 12 }}>{title}</h6>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        {[
          { label: 'Yesterday', value: yesterday },
          { label: 'Last Week', value: lastWeek },
          { label: 'Last 30 Days', value: last30d },
        ].map(({ label, value }) => (
          <div key={label} style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>
              {value != null ? `${value}${unit}` : '—'}
            </div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{label}</div>
            <RangeCaption label={label} />
          </div>
        ))}
      </div>
    </div>
  </Card>
);

const PerformanceMetrics = () => {
  const dispatch = useDispatch();
  const { performance, isPerformanceLoading } = useSelector((state) => state.dashboardV2);

  useEffect(() => {
    dispatch({ type: GET_DASHBOARD_V2_PERFORMANCE.REQUEST });
  }, [dispatch]);

  if (isPerformanceLoading || !performance) {
    return [0, 1, 2].map((key) => (
      <Col key={key} md={4} className="d-flex" style={{ marginBottom: 12 }}>
        <Card className="h-100 w-100">
          <div style={{ minHeight: 120, position: 'relative' }}>
            <Loader />
          </div>
        </Card>
      </Col>
    ));
  }

  // Columns only — the parent supplies the Row so spacing matches the rest of the section.
  return (
    <>
      <Col md={4} className="d-flex" style={{ marginBottom: 12 }}>
        <MetricCard
          title="Avg Revenue per Session (₹)"
          yesterday={performance.yesterday?.avgRevenue}
          lastWeek={performance.lastWeek?.avgRevenue}
          last30d={performance.last30d?.avgRevenue}
        />
      </Col>
      <Col md={4} className="d-flex" style={{ marginBottom: 12 }}>
        <MetricCard
          title="Avg Session Duration"
          yesterday={performance.yesterday?.avgDurationMin}
          lastWeek={performance.lastWeek?.avgDurationMin}
          last30d={performance.last30d?.avgDurationMin}
          unit=" min"
        />
      </Col>
      <Col md={4} className="d-flex" style={{ marginBottom: 12 }}>
        <Card className="h-100 w-100">
          <div style={{ padding: '8px 4px' }}>
            <h6 style={{ fontWeight: 600, marginBottom: 12 }}>Peak Usage Hours</h6>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              {[
                { label: 'Yesterday', value: performance.yesterday?.peakHour },
                { label: 'Last Week', value: performance.lastWeek?.peakHour },
                { label: 'Last 30 Days', value: performance.last30d?.peakHour },
              ].map(({ label, value }) => (
                <div key={label} style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{value || '—'}</div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{label}</div>
                  <RangeCaption label={label} />
                </div>
              ))}
            </div>
          </div>
        </Card>
      </Col>
    </>
  );
};

MetricCard.propTypes = {
  title: PropTypes.string,
  yesterday: PropTypes.number,
  lastWeek: PropTypes.number,
  last30d: PropTypes.number,
  unit: PropTypes.string,
};

MetricCard.defaultProps = {
  unit: '',
};

export default PerformanceMetrics;
